import React from "react";
import * as ReactDOM from "react-dom";
import qs from 'qs';
import { takeEvery, put, select, call } from 'redux-saga/effects';

import Notification from '../../components/Notifications/Notifications';

import * as actions from '../actions';
import * as actionTypes from '../actions/actionTypes';

import * as SUBSCRIPTIONS_ACTIONS from '../actions/subscriptions';

import * as AUTH_SELECTORS from '../selectors/auth';

import axios from '../../services/axios-client';

import { HTTP_METHOD } from '../../const/http/HTTP_METHOD';
import { 
    API_SUBSCRIPTION_AGREEMENT_ID_ROUTE,
    API_SUBSCRIPTION_SELL_ID_ROUTE
 } from '../../const/http/API_URL';
import { HTTP_STATUS_CODE } from '../../const/http/HTTP_STATUS_CODE';
import { PAYMENT_STATUS } from "../../const/payment/PAYMENT_STATUS";

// utility function
const transformData = (usages) => {
    let monthlyUsages = [];
    let weeklyUsages = [];

    usages.forEach((usage) => usage.periodicity === 'monthly' ? monthlyUsages.push(usage) : weeklyUsages.push(usage));

    return { monthlyUsages, weeklyUsages };
}

export function* getSubscriptionsList() {
    const { companyKey, userToken } = yield select(({ auth }) => auth);

    const getSubscriptionList = () => axios.request({
        method: 'GET',
        url: '/api/subscription/list',
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
            'company-api-key': companyKey,
            'user-access-token': userToken,
        },
    });

    try {
        const response = yield call(getSubscriptionList);

        yield put(actions.getSubscriptionsListSucceeded(response.data.result));
    } catch (error) {
        ReactDOM.render(
            <Notification type='error' error={error}/>,
            document.getElementById('notification')
        );
    }
}

export function* getSubscriptionDetails(action) {
    const { companyKey, userToken } = yield select(({ auth }) => auth);

    const getSubscriptionDetails = () => axios.request({
        method: 'GET',
        url: `/api/client/subscription/details?clientSubscriptionId=${action.clientSubscriptionId}`,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
            'company-api-key': companyKey,
            'user-access-token': userToken,
        },
    });

    try {
        const { data: { result } } = yield call(getSubscriptionDetails);

        const { monthlyUsages, weeklyUsages } = transformData(result.periods.current_usages);

        yield put(actions.setSubscriptionDetails(result));
        yield put(actions.setMonthlyUsages({
            usages: monthlyUsages,
            hasNext: result.periods.next_monthly,
            hasPrev: result.periods.prev_monthly,
        }));
        yield put(actions.setWeeklyUsages({
            usages: weeklyUsages,
            hasNext: result.periods.next_weekly,
            hasPrev: result.periods.prev_weekly,
        }));
    } catch (error) {
        ReactDOM.render(
            <Notification type='error' error={error}/>,
            document.getElementById('notification')
        );
    }
}

export function* getUsages(action) {
    const { companyKey, userToken } = yield select(({ auth }) => auth);

    const request = () => axios.request({
        method: 'GET',
        url: `/api/client/subscription/periods?clientSubscriptionId=${action.clientSubscriptionId}&startDate=${action.startDate}`,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
            'company-api-key': companyKey,
            'user-access-token': userToken,
        },
    });

    try {
        const { data: { result } } = yield call(request);

        const { monthlyUsages, weeklyUsages } = transformData(result.current_usages);

        yield put(actions.setWeeklyUsages({
            usages: weeklyUsages,
            hasNext: result.next_weekly,
            hasPrev: result.prev_weekly,
        }));
        yield put(actions.setMonthlyUsages({
            usages: monthlyUsages,
            hasNext: result.next_monthly,
            hasPrev: result.prev_monthly,
        }));

    } catch (error) {
        ReactDOM.render(
            <Notification type='error' error={error}/>,
            document.getElementById('notification')
        );
    }
}

export function* getConfirmText(action) {
    const { payload } = action;
    const {
        subscriptionId,
        startDate,
    } = payload;

    const { companyKey, userToken } = yield select(AUTH_SELECTORS.authSelector);
    const locale = yield select(state => state.locales.locale);

    try {
        const data = qs.stringify({ startDate });

        const res = yield call(axios.request, {
            method: HTTP_METHOD.POST,
            url: API_SUBSCRIPTION_AGREEMENT_ID_ROUTE({ subscriptionId }),
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
                'company-api-key': companyKey,
                'user-access-token': userToken,
                locale,
            },
            data,
        });

        const { data: { code, result } } = res;
        if (code === HTTP_STATUS_CODE.OK) {
            yield put(SUBSCRIPTIONS_ACTIONS.setConfirmText({ confirmText: result }))
        }
    } catch (error) {
        ReactDOM.render(
            <Notification
                type='error'
                error={error}
            />,
            document.getElementById('notification'),
        );
    }
}

export function* buySubscription(action) {
    const { payload } = action;
    const {
        subscriptionId,
        startDate,
    } = payload;

    const { companyKey, userToken } = yield select(AUTH_SELECTORS.authSelector);
    
    const data = qs.stringify({ 
        startDate,
        from_widget: 1,
    })
    yield put(SUBSCRIPTIONS_ACTIONS.setPaymentStatus({ status: PAYMENT_STATUS.PENDING }));

    try {
        const res = yield call(axios.request, {
            method: HTTP_METHOD.POST,
            url: API_SUBSCRIPTION_SELL_ID_ROUTE({ subscriptionId }),
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
                'company-api-key': companyKey,
                'user-access-token': userToken,
            },
            data,
        });

        const { data: { code, result: { paymentLink, id } } } = res;
        if (code === HTTP_STATUS_CODE.OK) {
            yield put(SUBSCRIPTIONS_ACTIONS.buySubscriptionsItemSucceeded({ id }));

            if (paymentLink) {
                const popup_window = window.open(paymentLink, '_blank');

                if (!popup_window) {
                    yield put(SUBSCRIPTIONS_ACTIONS.setPaymentLink({ paymentLink }));
                    return;
                }
            } else {
                yield put(SUBSCRIPTIONS_ACTIONS.setPaymentStatus({ status: PAYMENT_STATUS.OK }));
            }
        }
    } catch (error) {
        ReactDOM.render(
            <Notification
                type='error'
                error={error}
            />,
            document.getElementById('notification'),
        );
    }
}

export function* checkBuySubscriptionStatus({ id }) {
    try {
        const userToken = yield select(state => state.auth.userToken);
        const companyKey = yield select(state => state.auth.companyKey);

        yield axios.request({
            method: 'POST',
            url: '/api/payment/check-status/subscription',
            data: qs.stringify({
                subscription_id: id
            }),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'company-api-key': companyKey,
                'user-access-token': userToken
            }
        });
    } catch (error) {
        ReactDOM.render(
            <Notification type='error' error={error}/>,
            document.getElementById('notification')
        );
    }
}

export const watchSubscriptions = [
    takeEvery(actionTypes.GET_SUBSCRIPTIONS_LIST, getSubscriptionsList),
    takeEvery(actionTypes.GET_SUBSCRIPTION_DETAILS, getSubscriptionDetails),
    takeEvery(actionTypes.GET_USAGES, getUsages),
    takeEvery(actionTypes.SUBSCRIPTIONS_ITEM_BUY, buySubscription),
    takeEvery(actionTypes.SUBSCRIPTIONS_ITEM_CONFIRM_TEXT, getConfirmText),
    takeEvery(actionTypes.SUBSCRIPTIONS_SET_PAYMENT_STATUS, getSubscriptionsList)
];
